import { Formik, FormikProps, Form as FormikForm } from "formik";
import React, { useEffect, useRef } from "react";
import { Button, Col, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import DomainInput from "../../../../components/inputs/domainInput";
import ScrollToFieldError from "../../../../components/ScrollToFieldError";
import useIsDomainMode from "../../../../hooks/useIsDomainMode";
import {
  whiteLabelJRStep,
  whiteLabelStep,
} from "../../../../qraphql/globalVar/state";
import {
  useCreateOrUpdateWhiteLabelDomainMutation,
  useOrganizerWhiteLabelQuery,
} from "../../../../qraphql/whiteLabel/whiteLabel.hooks";
import { DomainType, WhiteLaelStepEnum } from "../../../../types";
import {
  validationDomain,
  validationSubdomain,
} from "../../../../validations/whiteLabel";
import Logo from "./img/add-domain.png";

interface IFirstStepProps {
  setStep: (step: number) => void;
  handleSetUserDomain: (domain: string) => void;
  userDomain: string;
}

interface IAddDomainForm {
  domain: string;
}

export default function FirstStep({
  setStep,
  handleSetUserDomain,
  userDomain,
}: IFirstStepProps) {
  const { t } = useTranslation();
  const formRef = useRef<FormikProps<IAddDomainForm>>(null);
  const isDomainMode = useIsDomainMode();

  const { data: whiteLabelInfo } = useOrganizerWhiteLabelQuery();
  const [
    createOrUpdateWhiteLabelDomain,
    {
      loading: createOrUpdateWhiteLabelDomainLoading,
      error: createOrUpdateWhiteLabelDomainError,
    },
  ] = useCreateOrUpdateWhiteLabelDomainMutation();

  useEffect(() => {
    const currentStep = document.querySelector("#domainStep1");
    if (currentStep) {
      currentStep.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      });
    }
  }, []);

  const onSubmit = (values: IAddDomainForm) => {
    createOrUpdateWhiteLabelDomain({
      variables: {
        domainType: isDomainMode ? DomainType.Domain : DomainType.Subdomain,
        whiteLabelId: whiteLabelInfo.organizer?.whiteLabel?.id,
        domain: values.domain,
      },
      onCompleted: () => {
        handleSetUserDomain(values.domain);
        setStep(2);
        whiteLabelStep(WhiteLaelStepEnum.MediaDesign);
        whiteLabelJRStep(8);
      },
    });
  };

  const initialValues: IAddDomainForm = {
    domain: userDomain,
  };

  return (
    <div className="mx-auto text-center border rounded-2">
      <Col xs={12} md={6} className="mx-auto p-4">
        <img
          src={Logo}
          loading="lazy"
          alt="Add Domain"
          className="img-fluid mb-4"
        />
        <Formik
          innerRef={formRef}
          initialValues={initialValues}
          onSubmit={onSubmit}
          validationSchema={
            isDomainMode ? validationDomain(t) : validationSubdomain(t)
          }
          enableReinitialize
        >
          {({ isValid, submitCount, touched, errors }) => {
            return (
              <FormikForm>
                <ScrollToFieldError />
                <DomainInput
                  label={
                    isDomainMode
                      ? t("whiteLabel.Domain Name")
                      : t("whiteLabel.Subdomain Name")
                  }
                  placeholder={
                    isDomainMode
                      ? "yourowndomain.com"
                      : "yoursubdomain.domain.com"
                  }
                  errorApollo={createOrUpdateWhiteLabelDomainError}
                  name="domain"
                  className={
                    touched.domain && errors.domain ? "is-invalid" : ""
                  }
                  style={{ zIndex: 2, backgroundImage: "none" }}
                  autoComplete="off"
                />

                <Button
                  variant="success"
                  className="w-100 text-white fw-bold py-2 my-4"
                  type="submit"
                  disabled={
                    (submitCount > 0 && !isValid) ||
                    createOrUpdateWhiteLabelDomainLoading
                  }
                >
                  <>
                    {t("whiteLabel.Continue")}
                    {createOrUpdateWhiteLabelDomainLoading && (
                      <Spinner
                        animation="border"
                        role="status"
                        size="sm"
                        className="ms-2"
                      />
                    )}
                  </>
                </Button>
              </FormikForm>
            );
          }}
        </Formik>
      </Col>
    </div>
  );
}
